import {
  format,
  isValid,
  isToday,
  isTomorrow,
  isThisWeek,
  isSameMonth,
  isSameYear,
  isBefore,
  startOfDay,
  endOfDay,
  parseISO,
  addDays,
  addMonths,
  differenceInCalendarDays,
} from "date-fns";
import moment from "moment";
import defaultAgenda1 from "../assets/images/default_agenda_1.jpg";
import { imagesURL } from "../constants/API";
import { AGENDA_CATEGORIES } from "../constants/agendaConstants";

/**
 * Formats a single date or date range without year (e.g. "Fri, Aug 17 - Sat, Aug 18" or "Fri, Aug 17")
 */
export const getFormattedDateRange = (startDate, endDate) => {
  if (!startDate) return "Date to be confirmed";
  const start = startDate instanceof Date ? startDate : parseISO(String(startDate).trim());
  if (!isValid(start)) return String(startDate);

  if (!endDate || endDate === startDate) {
    return format(start, "EEE, MMM d");
  }

  const end = endDate instanceof Date ? endDate : parseISO(String(endDate).trim());
  if (!isValid(end)) return format(start, "EEE, MMM d");

  if (start.getMonth() === end.getMonth() && start.getDate() === end.getDate()) {
    return format(start, "EEE, MMM d");
  }

  return `${format(start, "EEE, MMM d")} - ${format(end, "EEE, MMM d")}`;
};

/**
 * Compact date format optimized for sidebar event cards without year (e.g. "Sep 16 - 17" or "Sep 16")
 */
export const getSidebarFormattedDate = (startDate, endDate, time) => {
  if (!startDate) return time || "Date to be confirmed";
  const start = startDate instanceof Date ? startDate : parseISO(String(startDate).trim());
  if (!isValid(start)) return time || String(startDate);

  if (endDate && endDate !== startDate) {
    const end = endDate instanceof Date ? endDate : parseISO(String(endDate).trim());
    if (isValid(end)) {
      if (start.getMonth() === end.getMonth()) {
        const range = `${format(start, "MMM d")} - ${format(end, "d")}`;
        return time ? `${range} • ${time}` : range;
      }
      const range = `${format(start, "MMM d")} - ${format(end, "MMM d")}`;
      return time ? `${range} • ${time}` : range;
    }
  }

  if (time) return time;
  return format(start, "MMM d");
};

/**
 * Shortens countdown strings into compact notation (e.g. "Starts in 16 hrs 22 min" -> "16h 22m", "In 2 days" -> "2d")
 */
export const formatShortCountdown = (countdown) => {
  if (!countdown) return "";
  return String(countdown)
    .replace(/^starts\s+in\s+/i, "")
    .replace(/^in\s+/i, "")
    .replace(/\bhours?\b|\bhrs?\b/gi, "h")
    .replace(/\bminutes?\b|\bmins?\b/gi, "m")
    .replace(/\bdays?\b|\bdys?\b/gi, "d")
    .replace(/\bweeks?\b|\bwks?\b/gi, "w")
    .replace(/(\d+)\s+([hmwd])/gi, "$1$2")
    .trim();
};

/**
 * Formats a duration in minutes into a human-friendly string:
 * - < 60 mins -> "45 mins"
 * - < 24 hrs -> "1h 30m" or "2 hrs"
 * - >= 24 hrs (multi-day) -> "1 day", "2 days", "3d 4h", or "365 days"
 */
export const formatEventDuration = (totalMinutes, lang = null) => {
  if (!totalMinutes || isNaN(totalMinutes) || totalMinutes <= 0) return null;
  const mins = Math.round(totalMinutes);
  if (mins < 60) {
    const minsLabel = lang?.mins ? String(lang.mins).toLowerCase() : "mins";
    const minLabel = lang?.minute ? String(lang.minute).toLowerCase() : "min";
    return `${mins} ${mins === 1 ? minLabel : minsLabel}`;
  }
  if (mins < 1440) {
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    if (hours > 0 && remainingMins > 0) return `${hours}h ${remainingMins}m`;
    if (hours > 0) {
      const hrLabel = hours === 1
        ? (lang?.hour ? String(lang.hour).toLowerCase() : "hr")
        : (lang?.hrs || (lang?.hours ? String(lang.hours).toLowerCase() : "hrs"));
      return `${hours} ${hrLabel}`;
    }
    const minsLabel = lang?.mins ? String(lang.mins).toLowerCase() : "mins";
    return `${remainingMins} ${minsLabel}`;
  }
  const days = Math.floor(mins / 1440);
  const remainingHours = Math.floor((mins % 1440) / 60);
  const dayLabel = days === 1
    ? (lang?.day ? String(lang.day).toLowerCase() : "day")
    : (lang?.days ? String(lang.days).toLowerCase() : "days");
  if (days >= 7) {
    return `${days} ${dayLabel}`;
  }
  if (remainingHours > 0) {
    return `${days}d ${remainingHours}h`;
  }
  return `${days} ${dayLabel}`;
};

/**
 * Computes human-friendly duration for an agenda item from startDate/endDate objects, timestamps, or raw duration
 */
export const getAgendaItemDuration = (item, lang = null) => {
  if (!item) return null;

  if (item.startDateObj && item.endDateObj) {
    const diffMs = item.endDateObj.getTime() - item.startDateObj.getTime();
    if (diffMs > 0) {
      const totalMinutes = Math.round(diffMs / 60000);
      const formatted = formatEventDuration(totalMinutes, lang);
      if (formatted) return formatted;
    }
  }

  const startTs = item.raw?.start_timestamp || item.start_timestamp;
  const endTs = item.raw?.end_timestamp || item.end_timestamp;
  if (startTs && endTs && Number(endTs) > Number(startTs)) {
    const totalMinutes = Math.round((Number(endTs) - Number(startTs)) / 60);
    const formatted = formatEventDuration(totalMinutes, lang);
    if (formatted) return formatted;
  }

  const rawDuration = item.duration || item.raw?.duration || item.meta?.duration;
  if (rawDuration !== undefined && rawDuration !== null && rawDuration !== "") {
    if (typeof rawDuration === "number" && rawDuration > 0) {
      return formatEventDuration(rawDuration, lang);
    }
    const match = String(rawDuration).match(/\d+/);
    if (match) {
      const num = Number(match[0]);
      if (num > 0) return formatEventDuration(num, lang);
    }
  }

  return null;
};

/**
 * Dynamically computes a real-time countdown string for an event item.
 */
export const calculateItemCountdown = (item, referenceDate = new Date()) => {
  if (!item) return "";
  const statusLower = String(item.status || item.raw?.status || "").toLowerCase();
  if (statusLower === "started" || statusLower === "live") return "Started";
  if (statusLower === "ended") return "Ended";

  const now = referenceDate instanceof Date && !isNaN(referenceDate.getTime()) ? referenceDate : new Date();
  const startDateObj =
    item.startDateObj instanceof Date && !isNaN(item.startDateObj.getTime())
      ? item.startDateObj
      : item.date
        ? parseISO(item.date)
        : null;

  if (!startDateObj || !isValid(startDateObj)) return item.countdown || "";

  const nowMs = now.getTime();
  const startMs = startDateObj.getTime();
  const todayStart = startOfDay(now);

  if (startMs <= nowMs) {
    return "Started";
  }

  const diffCalendarDays = differenceInCalendarDays(
    startOfDay(startDateObj),
    todayStart
  );
  const diffMins = Math.floor((startMs - nowMs) / (1000 * 60));

  if (diffCalendarDays <= 0 || isToday(startDateObj)) {
    if (diffMins < 60) {
      return diffMins <= 1 ? "Starts in 1m" : `Starts in ${diffMins}m`;
    }
    const hrs = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `Starts in ${hrs}h ${mins}m` : `Starts in ${hrs}h`;
  } else if (diffCalendarDays === 1 || isTomorrow(startDateObj)) {
    if (diffMins < 24 * 60) {
      const hrs = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return mins > 0 ? `Starts in ${hrs}h ${mins}m` : `Starts in ${hrs}h`;
    }
    return "Tomorrow";
  } else if (diffCalendarDays >= 2 && diffCalendarDays <= 7) {
    return `In ${diffCalendarDays}d`;
  } else if (isSameMonth(startDateObj, now)) {
    const weeks = Math.ceil(diffCalendarDays / 7);
    return `In ${weeks}w`;
  } else if (!isSameYear(startDateObj, now) || diffCalendarDays > 365) {
    return `Upcoming in ${format(startDateObj, "yyyy")}`;
  } else {
    return `Upcoming in ${format(startDateObj, "MMM").toUpperCase()}`;
  }
};

/**
 * Determines the tag/badge information for an item in the Upcoming Sidebar:
 * - Live: Shows Live badge
 * - "later_this_month": Shows scheduled date (e.g. "Sep 24")
 * - "upcoming_..." (Month / Year): Returns null (no tag shown)
 * - Today / Tomorrow / This Week: Shows countdown (e.g. "16h 22m", "2d")
 */
export const getSidebarItemTagInfo = (item, groupKey, lang, referenceDate = new Date()) => {
  if (!item) return null;

  const dynamicCountdown = calculateItemCountdown(item, referenceDate);

  const isLive =
    item.status === "started" ||
    item.status === "live" ||
    String(item.raw?.status || "").toLowerCase() === "started" ||
    String(item.raw?.status || "").toLowerCase() === "live" ||
    dynamicCountdown === "Started";

  if (isLive) {
    return {
      type: "live",
      label: lang?.live || "Live",
      title: lang?.live_now || "Live Now",
      isLive: true,
    };
  }

  // If group is Upcoming in Month or Upcoming in Year -> do NOT show tag
  if (
    groupKey &&
    (groupKey.startsWith("upcoming_") ||
      groupKey === "upcoming_month" ||
      groupKey === "upcoming_year")
  ) {
    return null;
  }

  // If group is Later this Month -> show formatted date in the tag
  if (groupKey === "later_this_month") {
    let dateStr = "";
    if (item.startDateObj instanceof Date && !isNaN(item.startDateObj.getTime())) {
      dateStr = format(item.startDateObj, "MMM d");
    } else if (item.date) {
      try {
        const parsed = parseISO(String(item.date).trim());
        if (isValid(parsed)) {
          dateStr = format(parsed, "MMM d");
        }
      } catch (e) { }
    }
    if (!dateStr) dateStr = String(item.date || "");

    if (!dateStr) return null;

    return {
      type: "date",
      label: dateStr,
      title: `${lang?.scheduled_for || "Scheduled for"} ${dateStr}`,
      isLive: false,
    };
  }

  // For Today, Tomorrow, This Week -> show countdown if available
  const activeCountdown = dynamicCountdown || item.countdown;
  if (activeCountdown && activeCountdown !== "Started" && activeCountdown !== "Ended") {
    return {
      type: "countdown",
      label: formatShortCountdown(activeCountdown),
      title: `${lang?.starts_in || "Starts in"} ${activeCountdown}`,
      isLive: false,
    };
  }

  return null;
};

/**
 * Checks whether an event, webinar, or clinical session has started or is currently live.
 */
export const isEventLiveOrStarted = (event) => {
  if (!event) return false;

  const rawObj = event.raw || event;

  // 1. Explicit status check (status 1 = Live / in progress)
  const status = String(event.status || rawObj.status || event.meta?.status || "").toLowerCase();
  if (
    status === "started" ||
    status === "live" ||
    status === "in_progress" ||
    status === "1" ||
    event.status === 1 ||
    rawObj.status === 1
  ) {
    return true;
  }
  if (Boolean(event.is_live || event.isLive || rawObj.is_live || rawObj.isLive)) {
    return true;
  }

  // 1. Check scheduled date + time (takes priority over potentially stale start_timestamp)
  const dStr =
    event.schedule_date ||
    rawObj.schedule_date ||
    event.date ||
    rawObj.date ||
    event.start_date ||
    rawObj.start_date ||
    "";

  // Find human-readable time string first (avoid 10+ digit numeric unix timestamp strings like "1791439709")
  let tStr = "";
  const timeCandidates = [
    event.schedule_time,
    rawObj.schedule_time,
    event.start_time,
    rawObj.start_time,
    event.time,
    rawObj.time,
  ];

  for (const c of timeCandidates) {
    if (typeof c === "string" && c.trim() && !/^\d{9,}$/.test(c.trim())) {
      tStr = c.trim();
      break;
    }
  }

  if (dStr && tStr) {
    const parsed = moment(`${dStr} ${tStr}`, [
      "YYYY-MM-DD h:mm A",
      "YYYY-MM-DD hh:mm A",
      "YYYY-MM-DD h:mma",
      "YYYY-MM-DD hh:mma",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD h:mm",
      "YYYY-MM-DD",
    ]);
    if (parsed.isValid()) {
      return Date.now() >= parsed.valueOf();
    }
  } else if (dStr) {
    const parsed = moment(dStr, [
      "YYYY-MM-DD",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ss",
      "MM/DD/YYYY",
    ]);
    if (parsed.isValid()) {
      if (moment().isBefore(parsed, "day")) {
        return false;
      }
    }
  }

  // 3. Check startDateObj
  if (event.startDateObj instanceof Date && !isNaN(event.startDateObj.getTime())) {
    return Date.now() >= event.startDateObj.getTime();
  }

  // 4. Fallback to start_timestamp only if no schedule_date/date was present
  if (!dStr && (event.start_timestamp || rawObj.start_timestamp)) {
    const ts = Number(event.start_timestamp || rawObj.start_timestamp);
    if (!isNaN(ts) && ts > 0) {
      const startMs = ts > 100000000000 ? ts : ts * 1000;
      return Date.now() >= startMs;
    }
  }

  return false;
};

/**
 * Checks whether an event, webinar, or clinical session has completed or ended.
 */
export const isEventEnded = (event) => {
  if (!event) return false;
  if (event.collection === "completed" || event.isEnded) return true;
  const status = String(event.status || event.raw?.status || event.meta?.status || "").toLowerCase();
  return status === "ended" || status === "completed" || status === "2" || event.status === 2 || event.raw?.status === 2;
};

/**
 * Checks if the current user can edit this event (only owner and only BEFORE it ends).
 */
export const canEditEvent = (event, currentUserId) => {
  if (!event) return false;
  const isCreator =
    (currentUserId && String(event.user_id || event.user?.id || event.creator_id || event.userId) === String(currentUserId)) ||
    Boolean(event.isCreator || event.is_creator || event.is_owner || event.isHost || event.is_host);

  if (!isCreator) return false;
  if (isEventEnded(event) || isEventLiveOrStarted(event)) return false;
  return true;
};

/**
 * Extracts clean physical venue or city location string (e.g. "Madrid", "Hospital Clinic")
 * Returns empty string if the location is purely a mode keyword ("Online", "Offline", "In-Person", "Hybrid")
 */
export const getPhysicalLocation = (item) => {
  if (!item) return "";
  if (item.isOnline && !item.isHybrid) return "";
  const rawLoc = String(item.shortLocation || item.location || "").trim();
  if (!rawLoc) return "";
  const genericModes = ["online", "offline", "in-person", "in person", "inperson", "hybrid"];
  if (genericModes.includes(rawLoc.toLowerCase())) return "";
  return rawLoc;
};

/**
 * Detects recurrence pattern from an event object ('daily', 'weekly', 'monthly', or 'one_time')
 */
export const getEventRecurrence = (event) => {
  if (!event) return "one_time";
  const vis = String(event.visibility || "").toLowerCase().trim();
  if (vis === "daily" || vis === "diario" || vis === "diaria") return "daily";
  if (vis === "weekly" || vis === "semanal" || vis === "semana") return "weekly";
  if (vis === "monthly" || vis === "mensual" || vis === "mes") return "monthly";
  return "one_time";
};

/**
 * Expands recurring events (Daily, Weekly, Monthly) into discrete separate single-day occurrences
 * across a date range so that each recurring instance appears as an individual badge on its exact date cell.
 */
export const expandRecurringEvents = (
  events = [],
  rangeStart = null,
  rangeEnd = null,
  maxOccurrencesPerEvent = 60
) => {
  if (!Array.isArray(events)) return [];

  const expandedList = [];

  events.forEach((event) => {
    if (!event) return;

    const recurrence = getEventRecurrence(event);
    if (recurrence === "one_time") {
      expandedList.push(event);
      return;
    }

    const startD =
      event.startDateObj instanceof Date && !isNaN(event.startDateObj.getTime())
        ? event.startDateObj
        : parseISO(event.date);

    let limitD = null;
    const hasExplicitEndDate =
      event.endDate &&
      event.endDate !== event.date &&
      !isBefore(parseISO(event.endDate), startD);

    if (hasExplicitEndDate) {
      try {
        limitD = parseISO(event.endDate);
      } catch {
        limitD = addDays(startD, 60);
      }
    } else {
      if (recurrence === "daily") {
        limitD = addDays(startD, 45);
      } else if (recurrence === "weekly") {
        limitD = addDays(startD, 120);
      } else if (recurrence === "monthly") {
        limitD = addDays(startD, 365);
      } else {
        limitD = addDays(startD, 30);
      }
    }

    if (rangeEnd && isBefore(limitD, rangeEnd)) {
      limitD = rangeEnd;
    }

    let currentD = new Date(startD.getTime());
    let count = 0;

    const startHours = event.startDateObj?.getHours?.() ?? 9;
    const startMinutes = event.startDateObj?.getMinutes?.() ?? 0;
    const endHours = event.endTime ? parseInt(event.endTime.split(":")[0], 10) : startHours + 1;
    const endMinutes = event.endTime ? parseInt(event.endTime.split(":")[1], 10) : startMinutes;

    while (
      !isBefore(limitD, startOfDay(currentD)) &&
      count < maxOccurrencesPerEvent
    ) {
      const occurrenceDateStr = format(currentD, "yyyy-MM-dd");
      const currentStartOfDay = startOfDay(currentD);

      let group = "upcoming";
      const now = new Date();
      if (isBefore(currentStartOfDay, startOfDay(now))) {
        group = "past";
      } else if (isToday(currentStartOfDay)) {
        group = "today";
      } else if (isTomorrow(currentStartOfDay)) {
        group = "tomorrow";
      } else if (isThisWeek(currentStartOfDay, { weekStartsOn: 1 })) {
        group = "this_week";
      } else if (isSameMonth(currentStartOfDay, now)) {
        group = "later_this_month";
      } else {
        group = "upcoming";
      }

      const occStartDateObj = new Date(
        currentD.getFullYear(),
        currentD.getMonth(),
        currentD.getDate(),
        startHours,
        startMinutes
      );

      expandedList.push({
        ...event,
        id: `${event.id}_occ_${occurrenceDateStr}`,
        originalId: event.id,
        date: occurrenceDateStr,
        endDate: occurrenceDateStr,
        startDateObj: occStartDateObj,
        isRecurringInstance: true,
        recurrenceType: recurrence,
        group,
        tags: [
          recurrence === "daily" ? "Daily" : recurrence === "weekly" ? "Weekly" : "Monthly",
          event.visibility || "Public",
        ],
      });

      count++;

      if (recurrence === "daily") {
        currentD = addDays(currentD, 1);
      } else if (recurrence === "weekly") {
        currentD = addDays(currentD, 7);
      } else if (recurrence === "monthly") {
        currentD = addMonths(currentD, 1);
      } else {
        break;
      }
    }
  });

  return expandedList;
};

/**
 * Normalizes entity_type from API to frontend category key
 */
export const mapEntityTypeToCategoryType = (entityType) => {
  const raw = String(entityType || "").trim().toLowerCase();
  const normalizedType = raw.replace(/_/g, "-");

  if (
    normalizedType === "mentor-request" ||
    normalizedType === "mentor_request" ||
    normalizedType === "mentorship" ||
    normalizedType === "mentor" ||
    normalizedType === "mentor-session" ||
    raw.includes("mentor")
  ) {
    return "mentorship";
  }

  if (
    normalizedType === "clinical-session" ||
    normalizedType === "clinical_session" ||
    normalizedType === "clinical" ||
    raw.includes("clinical")
  ) {
    return "clinical_session";
  }

  if (normalizedType === "webinar" || raw.includes("webinar")) {
    return "webinar";
  }

  return "event";
};

/**
 * Maps frontend selectedCategory to backend entity_type query parameter
 */
export const getCategoryApiValue = (selectedCategory, collection = "calendar") => {
  if (!selectedCategory || selectedCategory === "all") {
    if (collection === "completed" || collection === "saved") {
      return "event,webinar,clinical-session,mentor_request";
    }
    return "event,webinar";
  }

  const categoryMap = {
    clinical_session: "clinical-session",
    "clinical-session": "clinical-session",
    mentorship: "mentor_request",
    mentor_request: "mentor_request",
    "mentor-request": "mentor_request",
  };

  return String(selectedCategory)
    .split(",")
    .map((category) => category.trim())
    .filter(Boolean)
    .map((category) => categoryMap[category] || category)
    .join(",");
};

const isValidAgendaDate = (value) =>
  value instanceof Date && !Number.isNaN(value.getTime());

const getDateFromTimestamp = (timestamp) => {
  const numericTimestamp = Number(timestamp);
  if (!Number.isFinite(numericTimestamp) || numericTimestamp <= 0) return null;

  const milliseconds =
    numericTimestamp > 100000000000 ? numericTimestamp : numericTimestamp * 1000;
  const value = new Date(milliseconds);
  return isValidAgendaDate(value) ? value : null;
};

const formatAgendaFormDate = (value, timestamp, fallback = "") => {
  if (value) {
    const normalizedValue = String(value).trim();
    const dayFirstMatch = normalizedValue.match(
      /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/
    );

    if (dayFirstMatch) {
      const [, firstValue, secondValue, year] = dayFirstMatch;
      const first = Number(firstValue);
      const second = Number(secondValue);
      const isMonthFirst = second > 12 && first <= 12;
      const month = isMonthFirst ? first : second;
      const day = isMonthFirst ? second : first;

      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
    }

    const parsedValue = parseISO(normalizedValue);
    if (isValidAgendaDate(parsedValue)) {
      return format(parsedValue, "yyyy-MM-dd");
    }

    const browserParsedValue = new Date(normalizedValue);
    if (isValidAgendaDate(browserParsedValue)) {
      return format(browserParsedValue, "yyyy-MM-dd");
    }
  }

  const timestampDate = getDateFromTimestamp(timestamp);
  if (timestampDate) return format(timestampDate, "yyyy-MM-dd");

  return fallback || "";
};

const formatAgendaFormTime = (value, timestamp, fallback = "") => {
  if (value) {
    const match = String(value)
      .trim()
      .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);

    if (match) {
      const [, hourValue, minute, meridiem] = match;
      let hour = Number(hourValue);

      if (meridiem) {
        if (hour === 12) hour = 0;
        if (meridiem.toUpperCase() === "PM") hour += 12;
      }

      if (hour >= 0 && hour <= 23 && Number(minute) <= 59) {
        return `${String(hour).padStart(2, "0")}:${minute}`;
      }
    }
  }

  const timestampDate = getDateFromTimestamp(timestamp);
  if (timestampDate) return format(timestampDate, "HH:mm");

  return fallback || "";
};

const getAgendaCategoryValue = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return value.name || value.title || value.label || value.id || "";
  }
  return String(value);
};

const isAgendaEmptyValue = (value) => {
  if (value === null || value === undefined || value === "") return true;

  return ["@empty_data_", "undefined", "null"].includes(
    String(value).trim().toLowerCase()
  );
};

export const getAgendaOptionalValue = (value, fallback = "") => {
  if (isAgendaEmptyValue(value)) return fallback;
  return value;
};

export const resolveAgendaImageUrl = (image, fallback = "") => {
  if (
    !image ||
    String(image).trim().toLowerCase() === "undefined" ||
    String(image).trim() === "null" ||
    String(image).trim() === ""
  ) {
    return fallback;
  }

  if (typeof image === "object") {
    const candidate =
      image.url ||
      image.src ||
      image.image ||
      image.cover ||
      image.cover_image ||
      image.cover_img ||
      image.media ||
      image.media_url ||
      (Array.isArray(image) ? image[0] : null);
    if (!candidate) return fallback;
    image = candidate;
  }

  const str = String(image).trim();
  if (!str || ["@empty_data_", "undefined", "null"].includes(str.toLowerCase())) {
    return fallback;
  }

  if (/^(?:data:|blob:|https?:\/\/)/i.test(str)) {
    return str;
  }

  const cleanPath = str.startsWith("/") ? str.slice(1) : str;
  return `https://djoejj0gd5srt.cloudfront.net/${cleanPath}`;
};

export const getAgendaImageUrl = (cover, fallback = null) => {
  return resolveAgendaImageUrl(cover, fallback);
};

const parseAgendaLatLng = (value) => {
  if (!value || value === "undefined,undefined") return null;

  if (typeof value === "object") {
    const lat = Number(value.lat);
    const lng = Number(value.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  const [latValue, lngValue] = String(value).split(",").map((item) => item.trim());
  const lat = Number(latValue);
  const lng = Number(lngValue);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const getAgendaApiId = (event) => {
  const rawId =
    event?.apiId ?? event?.raw_id ?? event?.raw?.id ?? event?.originalId ?? event?.id ?? null;

  if (rawId === null || rawId === undefined || rawId === "") return null;

  return String(rawId).replace(/^(?:evt|event|webinar|session|mentorship)-/i, "");
};

/**
 * Gets the backend event id from either a calendar item or an agenda event.
 */
export const getAgendaEventApiId = getAgendaApiId;

/**
 * Converts the full event-detail response into the Agenda drawer's edit model.
 * The list endpoint deliberately omits fields such as cover, links, questions,
 * price and address, so the detail response is the source of truth here.
 */
export const mapEventDetailForAgendaEditor = (detail, fallbackEvent = {}) => {
  const detailData =
    detail?.data?.data ||
    (detail?.data && !detail?.title && !detail?.id && !detail?.caption ? detail.data : detail) ||
    {};
  const fallbackStartDate = fallbackEvent.date || fallbackEvent.startDate || "";

  const rawStartDate =
    detailData.date ||
    detailData.start_date ||
    detailData.schedule_date ||
    detailData.scheduled_date ||
    "";
  const rawStartTimestamp =
    detailData.start_timestamp ||
    detailData.timestamp ||
    detailData.start_time_stamp ||
    "";
  const startDate = formatAgendaFormDate(
    rawStartDate,
    rawStartTimestamp,
    fallbackStartDate
  );

  const rawEndDate =
    detailData.end_date ||
    detailData.endDate ||
    rawStartDate ||
    "";
  const rawEndTimestamp =
    detailData.end_timestamp ||
    detailData.end_time_stamp ||
    "";
  const endDate = formatAgendaFormDate(
    rawEndDate,
    rawEndTimestamp,
    fallbackEvent.endDate || startDate
  );

  const rawStartTime =
    detailData.time ||
    detailData.start_time ||
    detailData.schedule_time ||
    detailData.startTime ||
    "";
  const startTime = formatAgendaFormTime(
    rawStartTime,
    rawStartTimestamp,
    fallbackEvent.startTime || ""
  );

  const rawEndTime =
    detailData.end_time ||
    detailData.endTime ||
    detailData.time_end ||
    "";
  const endTime = formatAgendaFormTime(
    rawEndTime,
    rawEndTimestamp,
    fallbackEvent.endTime || ""
  );

  let metaDetailObj = detailData.meta;
  if (typeof metaDetailObj === "string") {
    try {
      metaDetailObj = JSON.parse(metaDetailObj);
    } catch (e) {
      metaDetailObj = {};
    }
  }

  const rawDetailAvailability = String(
    detailData.availability ||
    metaDetailObj?.availability ||
    detailData.meta?.availability ||
    detailData.event_availability ||
    fallbackEvent.availability ||
    ""
  ).trim();

  const isDetailHybrid =
    Boolean(fallbackEvent.isHybrid) ||
    /hybrid/i.test(rawDetailAvailability) ||
    (/online/i.test(rawDetailAvailability) && /offline/i.test(rawDetailAvailability));

  const availability = isDetailHybrid
    ? "Hybrid"
    : getAgendaOptionalValue(detailData.availability, "") ||
    fallbackEvent.availability ||
    (fallbackEvent.isOnline ? "Online" : "In-Person");

  const category =
    getAgendaOptionalValue(getAgendaCategoryValue(detailData.subcategory), "") ||
    getAgendaOptionalValue(detailData.subcategory_name, "") ||
    getAgendaOptionalValue(detailData.subcategory_id, "") ||
    getAgendaOptionalValue(getAgendaCategoryValue(detailData.category), "") ||
    getAgendaOptionalValue(detailData.category_name, "") ||
    getAgendaOptionalValue(detailData.category_id, "") ||
    getAgendaOptionalValue(detailData.specialty, "") ||
    fallbackEvent.subtitle ||
    fallbackEvent.category ||
    "";

  const detailLocation = getAgendaOptionalValue(detailData.location, "");
  const detailShortLocation = getAgendaOptionalValue(
    detailData.short_location,
    ""
  );
  const location =
    detailLocation || detailShortLocation || fallbackEvent.location || "";
  const address =
    detailShortLocation || fallbackEvent.address || fallbackEvent.shortLocation || location;

  const detailPrice = getAgendaOptionalValue(
    detailData.price,
    getAgendaOptionalValue(fallbackEvent.priceValue, "")
  );
  const rawPrice =
    String(detailPrice).trim().toLowerCase() === "free" || Number(detailPrice) === 0
      ? ""
      : detailPrice;
  const hasPrice = !isAgendaEmptyValue(rawPrice);

  const rawCoverImg =
    detailData.cover ||
    detailData.cover_image ||
    detailData.cover_img ||
    detailData.coverImage ||
    detailData.image ||
    detailData.thumbnail ||
    (Array.isArray(detailData.media) ? detailData.media[0] : detailData.media) ||
    (Array.isArray(detailData.images) ? detailData.images[0] : detailData.images) ||
    fallbackEvent.cover ||
    fallbackEvent.cover_image ||
    fallbackEvent.cover_img ||
    fallbackEvent.image ||
    fallbackEvent.raw?.cover ||
    fallbackEvent.raw?.cover_image ||
    fallbackEvent.raw?.cover_img ||
    fallbackEvent.raw?.image ||
    "";

  const resolvedImage = getAgendaImageUrl(
    rawCoverImg,
    fallbackEvent.image || fallbackEvent.cover || fallbackEvent.cover_image || null
  );

  const description =
    detailData.description ||
    detailData.caption ||
    detailData.content ||
    detailData.details ||
    fallbackEvent.description ||
    "";

  const duration = String(
    detailData.duration ||
    detailData.time_limit ||
    detailData.limit ||
    fallbackEvent.duration ||
    "60"
  );

  return {
    ...fallbackEvent,
    id: fallbackEvent.id || detailData.id,
    apiId: detailData.id || getAgendaApiId(fallbackEvent),
    type:
      fallbackEvent.type ||
      (detailData.type === "clinical-session" ||
        detailData.type === "clinical_session" ||
        detailData.entity_type === "clinical_session"
        ? "clinical_session"
        : detailData.type === "webinar" || detailData.entity_type === "webinar"
          ? "webinar"
          : "event"),
    title: getAgendaOptionalValue(
      detailData.title || detailData.name || detailData.topic,
      fallbackEvent.title || ""
    ),
    subtitle: category,
    category: detailData.subcategory_id || detailData.category_id || fallbackEvent.category,
    host:
      getAgendaOptionalValue(detailData.organizer, "") ||
      detailData.user?.name ||
      detailData.user?.user_name ||
      detailData.doctor_name ||
      fallbackEvent.host ||
      "",
    hostRole: fallbackEvent.hostRole,
    image: resolvedImage,
    cover: resolvedImage,
    date: startDate,
    startDate,
    endDate,
    startTime,
    endTime,
    duration,
    time: startTime && endTime ? `${startTime} - ${endTime}` : fallbackEvent.time || "",
    location,
    shortLocation: detailShortLocation || fallbackEvent.shortLocation || location,
    address,
    lat_lng: parseAgendaLatLng(detailData.lat_lng) || fallbackEvent.lat_lng || null,
    isHybrid: isDetailHybrid,
    isOnline: !isDetailHybrid && (
      String(availability).toLowerCase().includes("online") ||
      fallbackEvent.isOnline ||
      false
    ),
    isOffline: !isDetailHybrid && !String(availability).toLowerCase().includes("online"),
    description,
    price: hasPrice ? `€${rawPrice}` : "Free",
    priceValue: rawPrice,
    visibility:
      getAgendaOptionalValue(detailData.visibility, "") ||
      fallbackEvent.visibility ||
      "Public",
    recurrence:
      getAgendaOptionalValue(detailData.recurrence, "") ||
      fallbackEvent.recurrence ||
      "One Time",
    availability,
    website: getAgendaOptionalValue(
      detailData.website || detailData.webinar_url || detailData.url,
      fallbackEvent.website || ""
    ),
    registrationLink: getAgendaOptionalValue(
      detailData.link || detailData.registration_link || detailData.join_url || detailData.meeting_link,
      fallbackEvent.registrationLink || ""
    ),
    audienceLimit: getAgendaOptionalValue(
      detailData.limit || detailData.audience_limit || detailData.max_attendees,
      fallbackEvent.audienceLimit ?? fallbackEvent.maxAttendees ?? ""
    ),
    password: getAgendaOptionalValue(
      detailData.password || detailData.passcode || detailData.room_password,
      fallbackEvent.password || ""
    ),
    questions: Array.isArray(detailData.questions)
      ? detailData.questions
      : Array.isArray(detailData.qa)
        ? detailData.qa
        : fallbackEvent.questions || [],
    speakers: Array.isArray(detailData.speakers)
      ? detailData.speakers
      : Array.isArray(detailData.speaker)
        ? detailData.speaker
        : fallbackEvent.speakers || [],
    agenda: Array.isArray(detailData.agenda)
      ? detailData.agenda
      : Array.isArray(detailData.sessions)
        ? detailData.sessions
        : fallbackEvent.agenda || [],
    user: detailData.user || fallbackEvent.user || null,
    raw: {
      ...(fallbackEvent.raw || {}),
      ...detailData,
    },
  };
};

/**
 * Normalizes an API calendar item into the frontend event model
 */
export const mapCalendarItem = (item, collection = "calendar") => {
  if (!item) return null;

  const type = mapEntityTypeToCategoryType(item.entity_type);
  const isMentorship = type === "mentorship";

  let startDateObj = null;
  if (item.start_timestamp) {
    const ts = Number(item.start_timestamp);
    startDateObj = new Date(ts > 100000000000 ? ts : ts * 1000);
  } else if (item.date) {
    if (item.time) {
      const combined = moment(`${item.date} ${item.time}`, [
        "YYYY-MM-DD h:mm A",
        "YYYY-MM-DD h:mm a",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD",
      ]);
      startDateObj = combined.isValid() ? combined.toDate() : parseISO(item.date);
    } else {
      startDateObj = parseISO(item.date);
    }
  }
  if (!isValid(startDateObj)) {
    startDateObj = new Date();
  }

  let endDateObj = null;
  if (item.end_timestamp) {
    const ts = Number(item.end_timestamp);
    endDateObj = new Date(ts > 100000000000 ? ts : ts * 1000);
  } else if (item.end_date) {
    if (item.end_time) {
      const combined = moment(`${item.end_date} ${item.end_time}`, [
        "YYYY-MM-DD h:mm A",
        "YYYY-MM-DD h:mm a",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD",
      ]);
      endDateObj = combined.isValid() ? combined.toDate() : parseISO(item.end_date);
    } else {
      endDateObj = parseISO(item.end_date);
    }
  } else {
    endDateObj = startDateObj;
  }
  if (!isValid(endDateObj)) {
    endDateObj = startDateObj;
  }

  const dateStr = item.date || format(startDateObj, "yyyy-MM-dd");
  const endDateStr = item.end_date || format(endDateObj, "yyyy-MM-dd");

  const startTimeFormatted = format(startDateObj, "h:mm a");
  const endTimeFormatted = format(endDateObj, "h:mm a");
  const timeStr = `${startTimeFormatted} - ${endTimeFormatted}`;

  // Sidebar grouping calculation
  let group = "upcoming";
  const now = new Date();
  const todayStart = startOfDay(now);
  const isOngoing =
    isBefore(startDateObj, todayStart) && !isBefore(endDateObj, todayStart);

  if (isOngoing || isToday(startDateObj)) {
    group = "today";
  } else if (isBefore(startDateObj, todayStart)) {
    group = "past";
  } else if (isTomorrow(startDateObj)) {
    group = "tomorrow";
  } else if (isThisWeek(startDateObj, { weekStartsOn: 1 })) {
    group = "this_week";
  } else if (isSameMonth(startDateObj, now)) {
    group = "later_this_month";
  } else {
    group = "upcoming";
  }

  let currentUserId = null;
  try {
    const signupData = JSON.parse(localStorage.getItem("SignupData") || "null");
    currentUserId = signupData?.data?.id || null;
  } catch (e) {
    currentUserId = null;
  }

  const isMentorUser = Boolean(
    isMentorship &&
    currentUserId &&
    (item.mentor?.id === currentUserId || item.meta?.mentor_id === currentUserId)
  );

  // Host & Role:
  // For mentorship:
  // - If current user is Mentor, show the Mentee (item.user)
  // - If current user is Mentee (or neutral), show the Mentor (item.mentor)
  const hostUser = isMentorship
    ? (isMentorUser ? (item.user || item.mentor || {}) : (item.mentor || item.user || {}))
    : (item.user || {});

  const hostName =
    hostUser.name ||
    [hostUser.first_name, hostUser.last_name].filter(Boolean).join(" ") ||
    (isMentorship ? "Mentorship Session" : "Specialist Host");

  const hostRole =
    hostUser.user_title ||
    hostUser.sub_type ||
    (hostUser.user_type
      ? hostUser.user_type.charAt(0).toUpperCase() + hostUser.user_type.slice(1)
      : "Medical Professional");

  const hostAvatar = resolveAgendaImageUrl(
    hostUser.image || hostUser.thumbnail || hostUser.profile_image,
    ""
  );

  // Cover image: fallback to host/partner avatar if cover is null for mentorship
  const coverImg = resolveAgendaImageUrl(
    item.cover,
    isMentorship && hostAvatar ? hostAvatar : defaultAgenda1
  );

  // Dynamic countdown string
  let countdown = "";
  const nowMs = Date.now();
  const startMs = startDateObj.getTime();

  const isCompletedEvent =
    collection === "completed" ||
    String(item.status || "").toLowerCase() === "ended" ||
    String(item.status || "").toLowerCase() === "completed" ||
    item.status === 2 ||
    item.status === "2";

  if (isCompletedEvent) {
    countdown = "Completed";
  } else if (item.status === "started" || item.status === "live") {
    countdown = "Started";
  } else if (item.status === "ended") {
    countdown = "Completed";
  } else if (startMs > nowMs) {
    const diffCalendarDays = differenceInCalendarDays(
      startOfDay(startDateObj),
      todayStart
    );
    const diffMins = Math.floor((startMs - nowMs) / (1000 * 60));

    if (diffCalendarDays <= 0 || isToday(startDateObj)) {
      if (diffMins < 60) {
        countdown = diffMins <= 1 ? "Starts in 1m" : `Starts in ${diffMins}m`;
      } else {
        const hrs = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        countdown = mins > 0 ? `Starts in ${hrs}h ${mins}m` : `Starts in ${hrs}h`;
      }
    } else if (diffCalendarDays === 1 || isTomorrow(startDateObj)) {
      if (diffMins < 24 * 60) {
        const hrs = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        countdown = mins > 0 ? `Starts in ${hrs}h ${mins}m` : `Starts in ${hrs}h`;
      } else {
        countdown = "Tomorrow";
      }
    } else if (diffCalendarDays >= 2 && diffCalendarDays <= 7) {
      countdown = `In ${diffCalendarDays}d`;
    } else if (isSameMonth(startDateObj, now)) {
      const weeks = Math.ceil(diffCalendarDays / 7);
      countdown = `In ${weeks}w`;
    } else if (!isSameYear(startDateObj, now) || diffCalendarDays > 365) {
      countdown = `Upcoming in ${format(startDateObj, "yyyy")}`;
    } else {
      countdown = `Upcoming in ${format(startDateObj, "MMM").toUpperCase()}`;
    }
  } else if (isToday(startDateObj)) {
    countdown = "Started";
  }

  const colorMap = {
    webinar: "linear-gradient(135deg, #3a0ca3 0%, #4361ee 100%)",
    clinical_session: "linear-gradient(135deg, #1d3557 0%, #457b9d 100%)",
    event: "linear-gradient(135deg, #061223 0%, #1BB9D3 100%)",
    mentorship: "linear-gradient(135deg, #78290f 0%, #ff7d00 100%)",
  };

  let metaObj = item.meta;
  if (typeof metaObj === "string") {
    try {
      metaObj = JSON.parse(metaObj);
    } catch (e) {
      metaObj = {};
    }
  }

  const rawAvailability = String(
    item.availability ||
    metaObj?.availability ||
    item.meta?.availability ||
    item.event_availability ||
    item.event_mode ||
    item.mode ||
    ""
  ).trim();

  const isHybrid =
    Boolean(item.is_hybrid || item.isHybrid) ||
    /hybrid/i.test(rawAvailability) ||
    (/online/i.test(rawAvailability) && /offline/i.test(rawAvailability)) ||
    (Boolean(item.is_online || item.isOnline) && Boolean(item.is_offline || item.isOffline));

  const isOffline =
    !isHybrid &&
    (Boolean(item.is_offline || item.isOffline) ||
      /offline|in[-_ ]?person|onsite|on-site/i.test(rawAvailability) ||
      Boolean(item.location && !/online/i.test(rawAvailability) && type !== "webinar"));

  const isOnline =
    !isHybrid &&
    !isOffline &&
    (type === "webinar" ||
      /online/i.test(rawAvailability) ||
      (!item.location && !isOffline));

  const availability = isHybrid ? "Hybrid" : isOnline ? "Online" : "In-Person";
  const displayLocation = item.location ? String(item.location).trim() : "";

  const isCreator = !isMentorship && Boolean(
    (currentUserId && String(item.user?.id || item.user_id || item.creator_id) === String(currentUserId)) ||
    item.isCreator ||
    item.is_creator ||
    item.is_owner
  );
  const isReserved = Boolean(item.is_reserved);
  const isJoined = isMentorship || (!isCreator && Boolean(item.is_joined)) || isReserved;

  const rawJoinedUsers = Array.isArray(item.joined_users)
    ? item.joined_users
    : item.joined_users && typeof item.joined_users === "object"
      ? Object.values(item.joined_users)
      : [];
  const joinedUsers = rawJoinedUsers
    .filter((u) => !isCreator || !currentUserId || String(u?.id) !== String(currentUserId))
    .map((u) => {
      const rawImage = u?.image || "";
      const avatarUrl = rawImage
        ? getAgendaImageUrl(rawImage, defaultAgenda1)
        : defaultAgenda1;
      const displayName =
        u?.name ||
        [u?.first_name, u?.last_name].filter(Boolean).join(" ") ||
        "Attendee";
      return {
        id: u?.id,
        name: displayName,
        image: avatarUrl,
      };
    });

  const rawAttendeesCount =
    typeof item.attendees_count === "number"
      ? item.attendees_count
      : typeof item.attendeesCount === "number"
        ? item.attendeesCount
        : typeof item.joined === "number"
          ? item.joined
          : typeof item.joined_count === "number"
            ? item.joined_count
            : typeof item.total_attendees === "number"
              ? item.total_attendees
              : typeof item.joined_users_count === "number"
                ? item.joined_users_count
                : null;

  const attendeesCount =
    joinedUsers.length > 0
      ? joinedUsers.length
      : rawAttendeesCount !== null
        ? (isCreator && rawAttendeesCount <= 1 && joinedUsers.length === 0 ? 0 : rawAttendeesCount)
        : !isCreator && isJoined
          ? 1
          : 0;

  const rawId = String(item.id ?? "");
  const prefix =
    type === "webinar"
      ? "webinar-"
      : type === "clinical_session" || type === "clinical-session"
        ? "session-"
        : type === "mentorship"
          ? "mentorship-"
          : "evt-";

  const formattedId =
    rawId.startsWith("webinar-") ||
      rawId.startsWith("session-") ||
      rawId.startsWith("evt-") ||
      rawId.startsWith("mentorship-")
      ? rawId
      : `${prefix}${rawId}`;

  return {
    id: formattedId,
    apiId: item.id,
    raw_id: item.id,
    originalId: item.id,
    title: item.title,
    subtitle: item.description ? item.description.slice(0, 60) + "..." : "",
    description: item.description || "",
    type,
    category: AGENDA_CATEGORIES[type.toUpperCase()] || AGENDA_CATEGORIES.EVENT,
    entity_type: item.entity_type,
    host: hostName,
    hostRole,
    hostAvatar,
    image: coverImg,
    date: dateStr,
    endDate: endDateStr,
    startDateObj,
    endDateObj,
    time: timeStr,
    startTime: format(startDateObj, "HH:mm"),
    endTime: format(endDateObj, "HH:mm"),
    location: displayLocation || (isOnline ? "Online" : isHybrid ? "Hybrid" : "In-Person"),
    shortLocation: displayLocation || (isOnline ? "Online" : isHybrid ? "Hybrid" : "In-Person"),
    isOnline,
    isHybrid,
    isOffline,
    availability,
    joinedUsers,
    attendeesCount,
    isReserved: Boolean(item.is_reserved),
    price: item.price ? `€${item.price}` : "Free",
    status: isCompletedEvent ? "completed" : (item.status || "upcoming"),
    isEnded: isCompletedEvent,
    collection,
    countdown,
    coverColor: "#F4F7FB",
    visibility: item.visibility || "Public",
    recurrence: item.visibility || "One Time",
    tags: [
      type === "webinar"
        ? "Webinar"
        : type === "clinical_session"
          ? "Clinical Session"
          : type === "mentorship"
            ? "Mentorship"
            : "Event",
      item.visibility || "Public",
    ],
    isJoined,
    isCreator,
    isSaved: (() => {
      const rawFav =
        item.is_favorite ??
        item.favorite ??
        item.isFavorite ??
        item.isFavorited ??
        item.is_saved ??
        item.isSaved ??
        item.is_like ??
        item.isLike ??
        item.is_save ??
        item.isSave;
      if (collection === "saved") {
        return rawFav !== false && rawFav !== 0 && rawFav !== "0";
      }
      return Boolean(
        rawFav === 1 || rawFav === true || rawFav === "1" || rawFav === "true"
      );
    })(),
    isFavorited: (() => {
      const rawFav =
        item.is_favorite ??
        item.favorite ??
        item.isFavorite ??
        item.isFavorited ??
        item.is_saved ??
        item.isSaved ??
        item.is_like ??
        item.isLike ??
        item.is_save ??
        item.isSave;
      if (collection === "saved") {
        return rawFav !== false && rawFav !== 0 && rawFav !== "0";
      }
      return Boolean(
        rawFav === 1 || rawFav === true || rawFav === "1" || rawFav === "true"
      );
    })(),
    is_favorite: (() => {
      const rawFav =
        item.is_favorite ??
        item.favorite ??
        item.isFavorite ??
        item.isFavorited ??
        item.is_saved ??
        item.isSaved ??
        item.is_like ??
        item.isLike ??
        item.is_save ??
        item.isSave;
      if (collection === "saved") {
        return rawFav !== false && rawFav !== 0 && rawFav !== "0";
      }
      return Boolean(
        rawFav === 1 || rawFav === true || rawFav === "1" || rawFav === "true"
      );
    })(),
    group,
    meta: item.meta || {},
    user: item.user || null,
    mentor: item.mentor || null,
    raw: item,
  };
};

/**
 * Groups events for sidebar navigation (Today, Tomorrow, This Week, Later this Month, Upcoming Month/Year)
 * Only displays schedules that the user has joined or created, filtered by the schedule category.
 * Also expands recurring events so upcoming recurring instances are visible under their respective dates.
 * Automatically excludes schedules that have already reached their start time or ended.
 */
export const groupEventsForSidebar = (
  events = [],
  scheduleCategory = "all",
  isDirectUpcoming = false,
  lang = null,
  referenceDate = new Date()
) => {
  const now = referenceDate instanceof Date && !isNaN(referenceDate.getTime()) ? referenceDate : new Date();
  const today = now;
  const expandedEvents = expandRecurringEvents(events, today, addDays(today, 120));

  const filteredEvents = expandedEvents.filter((e) => {
    if (!isDirectUpcoming && !e.isJoined && !e.isCreator) {
      return false;
    }
    if (scheduleCategory && scheduleCategory !== "all") {
      return e.type === scheduleCategory;
    }
    return true;
  });

  const todayPrefix = lang?.today || "Today";
  const tomorrowPrefix = lang?.tomorrow || "Tomorrow";
  const thisWeekTitle = lang?.this_week || "This Week";
  const laterThisMonthTitle = lang?.later_this_month || "Later this Month";
  const upcomingInPrefix = lang?.upcoming_in || "Upcoming in";

  const standardGroups = [
    {
      groupKey: "today",
      title: `${todayPrefix} - ${format(today, "EEE, MMM d")}`,
      items: [],
    },
    {
      groupKey: "tomorrow",
      title: `${tomorrowPrefix} - ${format(addDays(today, 1), "EEE, MMM d")}`,
      items: [],
    },
    {
      groupKey: "this_week",
      title: thisWeekTitle,
      items: [],
    },
    {
      groupKey: "later_this_month",
      title: laterThisMonthTitle,
      items: [],
    },
  ];

  const dynamicFutureGroups = {};

  filteredEvents.forEach((item) => {
    const d =
      item.startDateObj instanceof Date && !isNaN(item.startDateObj.getTime())
        ? item.startDateObj
        : item.date
          ? parseISO(item.date)
          : null;

    if (!d || !isValid(d)) return;

    // Filter out items that have already reached their start time or ended:
    // When an event reaches its start time (d <= now), it is removed from Upcoming Agenda in real time!
    if (
      d.getTime() <= now.getTime() ||
      item.status === "started" ||
      item.status === "live" ||
      item.status === "ended" ||
      String(item.raw?.status || "").toLowerCase() === "started" ||
      String(item.raw?.status || "").toLowerCase() === "live"
    ) {
      return; // Skip started/live/ended schedules
    }

    if (isToday(d) || item.group === "today") {
      standardGroups[0].items.push(item);
    } else if (isTomorrow(d) || item.group === "tomorrow") {
      standardGroups[1].items.push(item);
    } else if (isThisWeek(d, { weekStartsOn: 1 }) || item.group === "this_week") {
      standardGroups[2].items.push(item);
    } else if (isSameMonth(d, today) || item.group === "later_this_month") {
      standardGroups[3].items.push(item);
    } else if (isSameYear(d, today)) {
      const monthKey = `upcoming_${format(d, "yyyy_MM")}`;
      const monthTitle = `${upcomingInPrefix} ${format(d, "MMM").toUpperCase()}`;
      if (!dynamicFutureGroups[monthKey]) {
        dynamicFutureGroups[monthKey] = {
          groupKey: monthKey,
          title: monthTitle,
          items: [],
          sortDate: d,
        };
      }
      dynamicFutureGroups[monthKey].items.push(item);
    } else {
      const yearKey = `upcoming_${format(d, "yyyy")}`;
      const yearTitle = `${upcomingInPrefix} ${format(d, "yyyy")}`;
      if (!dynamicFutureGroups[yearKey]) {
        dynamicFutureGroups[yearKey] = {
          groupKey: yearKey,
          title: yearTitle,
          items: [],
          sortDate: d,
        };
      }
      dynamicFutureGroups[yearKey].items.push(item);
    }
  });

  const futureList = Object.values(dynamicFutureGroups).sort(
    (a, b) => a.sortDate - b.sortDate
  );

  return [...standardGroups, ...futureList].filter((g) => g.items.length > 0);
};

/**
 * Filter events by selected category
 */
export const filterEventsByCategory = (events = [], selectedCategory = "all") => {
  if (!Array.isArray(events)) return [];
  if (!selectedCategory || selectedCategory === "all") return events;
  return events.filter((e) => e.type === selectedCategory);
};

/**
 * Packs events into horizontal calendar lanes across a range of `totalDays`
 * consecutive days starting at `rangeStart` (7 for a week row, 1 for a single
 * day's all-day strip, etc.)
 */
export const layoutWeekEvents = (
  weekEvents = [],
  weekStart,
  maxLanes = 2,
  totalDays = 7
) => {
  const positioned = weekEvents.map((event) => {
    const start = startOfDay(parseISO(event.date));
    const end = event.endDate ? endOfDay(parseISO(event.endDate)) : endOfDay(start);

    const rawStartCol = differenceInCalendarDays(start, weekStart) + 1;
    const rawEndCol = differenceInCalendarDays(end, weekStart) + 1;

    const startCol = Math.max(1, Math.min(totalDays, rawStartCol));
    const endCol = Math.max(1, Math.min(totalDays, rawEndCol));
    const span = endCol - startCol + 1;

    const isStartThisWeek = rawStartCol >= 1;
    const isEndThisWeek = rawEndCol <= totalDays;

    return {
      ...event,
      startCol,
      endCol,
      span,
      isStartThisWeek,
      isEndThisWeek,
    };
  });

  // Sort: multi-day spanning events first, then by start column
  positioned.sort((a, b) => {
    if (b.span !== a.span) return b.span - a.span;
    return a.startCol - b.startCol;
  });

  const lanes = [];
  const placedEvents = [];
  const hiddenCountByCol = Array(totalDays).fill(0);

  positioned.forEach((evt) => {
    let targetLane = -1;
    for (let l = 0; l < lanes.length; l++) {
      let fits = true;
      for (let c = evt.startCol - 1; c < evt.endCol; c++) {
        if (lanes[l][c]) {
          fits = false;
          break;
        }
      }
      if (fits) {
        targetLane = l;
        break;
      }
    }

    if (targetLane === -1) {
      targetLane = lanes.length;
      lanes.push(Array(7).fill(false));
    }

    for (let c = evt.startCol - 1; c < evt.endCol; c++) {
      lanes[targetLane][c] = true;
    }

    if (targetLane < maxLanes) {
      placedEvents.push({ ...evt, laneIndex: targetLane });
    } else {
      for (let c = evt.startCol - 1; c < evt.endCol; c++) {
        hiddenCountByCol[c]++;
      }
    }
  });

  return { placedEvents, hiddenCountByCol };
};

const parseTimeToMinutes = (timeStr, fallback = 0) => {
  if (!timeStr) return fallback;
  const [hourPart, minutePart] = String(timeStr).split(":");
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  if (!Number.isFinite(hour)) return fallback;
  return hour * 60 + (Number.isFinite(minute) ? minute : 0);
};

/**
 * Packs a single day's timed events into side-by-side overlap columns for a
 * Day/Week time-grid view (Google/Apple Calendar style). Returns each event
 * with startMin/endMin (minutes from midnight), plus `col`/`totalCols` so the
 * caller can position it as `left: col/totalCols`, `width: 1/totalCols`.
 *
 * When more than `maxLanes` events overlap at once, only `maxLanes - 1` are
 * placed individually and the rest are folded into one `overflowGroups` entry
 * (spanning the whole overlapping cluster) so pills never shrink to unreadable
 * slivers — the caller renders that as a single "+N more" chip instead.
 */
export const layoutTimedEvents = (dayEvents = [], minDurationMinutes = 30, maxLanes = 4) => {
  const prepared = dayEvents.map((event) => {
    let startMin = parseTimeToMinutes(event.startTime, 9 * 60);
    let endMin = parseTimeToMinutes(event.endTime, startMin + minDurationMinutes);
    if (endMin <= startMin) endMin = startMin + minDurationMinutes;

    startMin = Math.max(0, Math.min(startMin, 24 * 60 - 15));
    endMin = Math.max(startMin + 15, Math.min(endMin, 24 * 60));

    return { ...event, startMin, endMin };
  });

  prepared.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const placed = [];
  const overflowGroups = [];
  let clusterEvents = [];
  let clusterColumnsEnd = [];
  let clusterMaxEnd = -Infinity;
  let clusterStartMin = null;

  const flushCluster = () => {
    if (!clusterEvents.length) return;
    const columnsUsed = clusterColumnsEnd.length;

    if (columnsUsed <= maxLanes) {
      clusterEvents.forEach((evt) => placed.push({ ...evt, totalCols: columnsUsed }));
    } else {
      const visibleLanes = Math.max(1, maxLanes - 1);
      const visible = clusterEvents.filter((evt) => evt.col < visibleLanes);
      const hidden = clusterEvents.filter((evt) => evt.col >= visibleLanes);

      visible.forEach((evt) => placed.push({ ...evt, totalCols: maxLanes }));

      if (hidden.length) {
        overflowGroups.push({
          key: `overflow-${clusterStartMin}-${clusterMaxEnd}`,
          startMin: clusterStartMin,
          endMin: clusterMaxEnd,
          count: hidden.length,
          events: hidden,
          col: visibleLanes,
          totalCols: maxLanes,
        });
      }
    }

    clusterEvents = [];
    clusterColumnsEnd = [];
    clusterMaxEnd = -Infinity;
    clusterStartMin = null;
  };

  prepared.forEach((evt) => {
    if (evt.startMin >= clusterMaxEnd) {
      flushCluster();
    }
    if (clusterStartMin === null) clusterStartMin = evt.startMin;

    let colIndex = clusterColumnsEnd.findIndex((endMin) => endMin <= evt.startMin);
    if (colIndex === -1) {
      colIndex = clusterColumnsEnd.length;
      clusterColumnsEnd.push(evt.endMin);
    } else {
      clusterColumnsEnd[colIndex] = evt.endMin;
    }

    clusterEvents.push({ ...evt, col: colIndex });
    clusterMaxEnd = Math.max(clusterMaxEnd, evt.endMin);
  });
  flushCluster();

  return { placed, overflowGroups };
};

/**
 * Returns customized title, description, and actionLabel for AgendaEmptyState
 * based on selected category and variant (explore vs sidebar).
 */
export const getAgendaEmptyStateDetails = (
  category = "all",
  variant = "explore",
  lang = null
) => {
  const normalizedCategory = String(category || "all").toLowerCase().trim();

  if (variant === "saved") {
    switch (normalizedCategory) {
      case "clinical_session":
      case "clinical-session":
        return {
          title: lang?.no_saved_clinical_sessions || "No Saved Clinical Sessions",
          description:
            lang?.no_saved_clinical_sessions_desc ||
            "You haven't saved any clinical sessions yet. Bookmark clinical sessions to easily find them here.",
        };
      case "webinar":
        return {
          title: lang?.no_saved_webinars || "No Saved Webinars",
          description:
            lang?.no_saved_webinars_desc ||
            "You haven't saved any webinars yet. Bookmark webinars to easily find them here.",
        };
      case "event":
        return {
          title: lang?.no_saved_events || "No Saved Events",
          description:
            lang?.no_saved_events_desc ||
            "You haven't saved any events yet. Bookmark events to easily find them here.",
        };
      case "mentorship":
        return {
          title: lang?.no_saved_mentorships || "No Saved Mentorships",
          description:
            lang?.no_saved_mentorships_desc ||
            "You haven't saved any mentorship sessions yet. Bookmark mentorship sessions to easily find them here.",
        };
      case "all":
      default:
        return {
          title: lang?.no_saved_agendas || "No Saved Agendas",
          description:
            lang?.no_saved_agendas_desc ||
            "You haven't saved any agendas yet. Bookmark events, webinars, or sessions to easily find them here later.",
        };
    }
  }

  if (variant === "completed") {
    switch (normalizedCategory) {
      case "clinical_session":
      case "clinical-session":
        return {
          title: lang?.no_completed_clinical_sessions || "No Completed Clinical Sessions",
          description:
            lang?.no_completed_clinical_sessions_desc ||
            "You haven't attended or completed any clinical sessions yet. Past sessions will appear here.",
        };
      case "webinar":
        return {
          title: lang?.no_completed_webinars || "No Completed Webinars",
          description:
            lang?.no_completed_webinars_desc ||
            "You haven't attended or completed any webinars yet. Past webinars will appear here.",
        };
      case "event":
        return {
          title: lang?.no_completed_events || "No Completed Events",
          description:
            lang?.no_completed_events_desc ||
            "You haven't attended or completed any events yet. Past events will appear here.",
        };
      case "mentorship":
        return {
          title: lang?.no_completed_mentorships || "No Completed Mentorships",
          description:
            lang?.no_completed_mentorships_desc ||
            "You haven't completed any mentorship sessions yet. Past mentorship sessions will appear here.",
        };
      case "all":
      default:
        return {
          title: lang?.no_completed_agendas || "No Completed Agendas",
          description:
            lang?.no_completed_agendas_desc ||
            "You haven't completed or attended any agendas yet. Past and completed activities will appear here.",
        };
    }
  }

  if (variant === "sidebar") {
    switch (normalizedCategory) {
      case "clinical_session":
      case "clinical-session":
        return {
          title: lang?.no_upcoming_clinical_sessions || "No Upcoming Clinical Sessions",
          description:
            lang?.no_upcoming_clinical_sessions_desc ||
            "There are no clinical sessions scheduled in your upcoming agenda. Schedule a new session or join upcoming clinical discussions.",
          actionLabel: lang?.create_agenda || "Create Event",
        };
      case "webinar":
        return {
          title: lang?.no_upcoming_webinars || "No Upcoming Webinars",
          description:
            lang?.no_upcoming_webinars_desc ||
            "There are no webinars scheduled in your upcoming agenda. Host a new webinar or explore upcoming webinars to join.",
          actionLabel: lang?.create_agenda || "Create Event",
        };
      case "event":
        return {
          title: lang?.no_upcoming_events || "No Upcoming Events",
          description:
            lang?.no_upcoming_events_desc ||
            "There are no events scheduled in your upcoming agenda. Create a new event or explore available events to participate.",
          actionLabel: lang?.create_agenda || "Create Event",
        };
      case "mentorship":
        return {
          title: lang?.no_upcoming_mentorships || "No Upcoming Mentorships",
          description:
            lang?.no_upcoming_mentorships_desc ||
            "There are no mentorship sessions scheduled in your upcoming agenda. Connect with mentors or schedule your next session.",
          actionLabel: lang?.create_agenda || "Create Event",
        };
      case "all":
      default:
        return {
          title: lang?.no_upcoming_agendas || "No Upcoming Agendas",
          description:
            lang?.no_upcoming_agendas_desc ||
            "There are no events or webinars scheduled right now. Check back soon for new opportunities to learn, connect, and stay up to date.",
          actionLabel: lang?.create_agenda || "Create Event",
        };
    }
  }

  // Default: Explore Grid View
  switch (normalizedCategory) {
    case "clinical_session":
    case "clinical-session":
      return {
        title: lang?.no_clinical_sessions_to_explore || "No Clinical Sessions to explore yet",
        description:
          lang?.no_clinical_sessions_to_explore_desc ||
          "There are no clinical sessions available right now. Stay tuned or switch categories to explore other activities.",
      };
    case "webinar":
      return {
        title: lang?.no_webinars_to_explore || "No Webinars to explore yet",
        description:
          lang?.no_webinars_to_explore_desc ||
          "There are no webinars available right now. Stay tuned or switch categories to explore other activities.",
      };
    case "event":
      return {
        title: lang?.no_events_to_explore || "No Events to explore yet",
        description:
          lang?.no_events_to_explore_desc ||
          "There are no events available right now. Stay tuned or switch categories to explore other activities.",
      };
    case "mentorship":
      return {
        title: lang?.no_mentorships_to_explore || "No Mentorships to explore yet",
        description:
          lang?.no_mentorships_to_explore_desc ||
          "There are no mentorship sessions available right now. Stay tuned or switch categories to explore other activities.",
      };
    case "all":
    default:
      return {
        title: lang?.no_agendas_to_explore || "No agendas to explore yet",
        description:
          lang?.no_agendas_to_explore_desc ||
          "Stay tuned for upcoming experiences designed to keep you informed and connected.",
      };
  }
};

/**
 * Sorts events so that joined/reserved/created events appear at the top
 */
export const sortEventsJoinedFirst = (events = []) => {
  if (!Array.isArray(events)) return [];
  return [...events].sort((a, b) => {
    const aJoined = Boolean(a?.isJoined || a?.isReserved || a?.isCreator);
    const bJoined = Boolean(b?.isJoined || b?.isReserved || b?.isCreator);

    if (aJoined && !bJoined) return -1;
    if (!aJoined && bJoined) return 1;
    return 0;
  });
};

/**
 * Unlocks body and document scrolling by removing .modal-open classes, inline overflow,
 * and padding styles left behind by Bootstrap/Reactstrap modals, custom drawers, or unmounting dialogs.
 * Executes immediately, on next RAF, and at staged intervals (50ms, 250ms, 400ms) to ensure
 * asynchronous modal unmount transitions don't leave the document frozen.
 */
export const releaseAgendaScrollLock = (force = false) => {
  if (typeof document === "undefined") return;

  const performRelease = () => {
    if (!force) {
      const openModal = document.querySelector(".modal.show:not(.fade)");
      const openDrawer = document.querySelector(".agenda-drawer-panel, .agenda-invite-panel");
      if (openModal || openDrawer) return;
    }

    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("overflow-y");
    document.body.style.removeProperty("overflow-x");
    document.body.style.removeProperty("padding-right");
    document.documentElement.style.removeProperty("overflow");
    document.documentElement.style.removeProperty("overflow-y");
    document.documentElement.style.removeProperty("overflow-x");
  };

  performRelease();
  if (typeof window !== "undefined") {
    window.requestAnimationFrame(performRelease);
    setTimeout(performRelease, 50);
    setTimeout(performRelease, 250);
    setTimeout(performRelease, 400);
  }
};

export { extractApiErrorMessage } from "./apiUtils";

